<script setup>
import { ref, watch, computed } from 'vue'
import axios from '../axios.js'
import { useUserStore } from '../stores/userStore'

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['close'])
const userStore = useUserStore()
const canCompleteTickets = computed(() => Number(userStore.currentUser?.id) === 6)

const activeTab = ref('new')
const description = ref('')
const isLoading = ref(false)
const error = ref(null)
const successMessage = ref(null)

// My tickets
const myTickets = ref([])
const loadingTickets = ref(false)
const ticketsError = ref(null)

const fetchMyTickets = async () => {
  loadingTickets.value = true
  ticketsError.value = null
  try {
    const { data } = await axios.get('/api/support-tickets')
    myTickets.value = data.data || []
  } catch (err) {
    ticketsError.value = err.response?.data?.message || 'Failed to load tickets'
    myTickets.value = []
  } finally {
    loadingTickets.value = false
  }
}

watch(activeTab, (tab) => {
  if (tab === 'list') {
    fetchMyTickets()
  }
})

watch(() => props.show, (isShow) => {
  if (isShow) {
    fetchMyTickets()
  }
})

const createTicket = async () => {
  if (!description.value.trim()) {
    error.value = 'Please enter a description'
    return
  }

  isLoading.value = true
  error.value = null
  successMessage.value = null

  try {
    const text = description.value.trim()
    await axios.post('/api/support-tickets/send', {
      description: text
    })

    successMessage.value = 'Ticket submitted successfully!'
    description.value = ''
    fetchMyTickets()

    setTimeout(() => {
      activeTab.value = 'list'
      successMessage.value = null
    }, 800)
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to submit ticket. Please try again.'
  } finally {
    isLoading.value = false
  }
}

const resetForm = () => {
  description.value = ''
  error.value = null
  successMessage.value = null
  activeTab.value = 'new'
  replyText.value = {}
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getStatusBadgeClass = (status) => {
  const map = {
    open: 'bg-amber-100 text-amber-800',
    in_progress: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-600'
  }
  return map[status] || 'bg-gray-100 text-gray-600'
}

const completingTicketId = ref(null)
const completeTicket = async (ticketId) => {
  completingTicketId.value = ticketId
  try {
    await axios.patch(`/api/support-tickets/${ticketId}/complete`)
    const ticket = myTickets.value.find(t => t.id === ticketId)
    if (ticket) ticket.status = 'completed'
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to complete ticket'
  } finally {
    completingTicketId.value = null
  }
}

const uncompleteTicket = async (ticketId) => {
  completingTicketId.value = ticketId
  try {
    await axios.patch(`/api/support-tickets/${ticketId}/uncomplete`)
    const ticket = myTickets.value.find(t => t.id === ticketId)
    if (ticket) ticket.status = 'open'
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to reopen ticket'
  } finally {
    completingTicketId.value = null
  }
}

// Replies (user 6 only can send)
const replyText = ref({})
const sendingReplyTicketId = ref(null)
const setReply = (ticketId, val) => {
  replyText.value = { ...replyText.value, [ticketId]: val }
}
const sendReply = async (ticketId) => {
  const text = (replyText.value[ticketId] || '').trim()
  if (!text) return
  sendingReplyTicketId.value = ticketId
  try {
    const { data } = await axios.post(`/api/support-tickets/${ticketId}/reply`, { message: text })
    const ticket = myTickets.value.find(t => t.id === ticketId)
    if (ticket) {
      if (!ticket.replies) ticket.replies = []
      ticket.replies.push(data.data)
    }
    setReply(ticketId, '')
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to send reply'
  } finally {
    sendingReplyTicketId.value = null
  }
}
</script>

<template>
  <Teleport to="body">
    <div 
      v-if="show" 
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30"
      @click.self="emit('close'); resetForm()"
    >
      <div 
        class="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden text-left flex flex-col"
        style="min-width: 400px; max-width: 480px; max-height: 85vh;"
        @click.stop
      >
        <!-- Header -->
        <div class="px-5 py-4 border-b border-gray-100 bg-gradient-to-br from-pink-50 to-white shrink-0">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-12 h-12 rounded-xl bg-pink-500/10 flex items-center justify-center">
                <i class="fas fa-ticket-alt text-pink-500 text-lg"></i>
              </div>
              <div>
                <h3 class="font-semibold text-gray-800 text-lg">Tickets</h3>
                <p class="text-sm text-gray-500">Submit or view your tickets</p>
              </div>
            </div>
            <button 
              @click="emit('close'); resetForm()"
              class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Tabs -->
          <div class="flex gap-1 mt-4 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              @click="activeTab = 'new'"
              class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors"
              :class="activeTab === 'new' ? 'bg-white text-pink-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'"
            >
              <i class="fas fa-plus mr-1.5"></i>
              New ticket
            </button>
            <button
              type="button"
              @click="activeTab = 'list'"
              class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors"
              :class="activeTab === 'list' ? 'bg-white text-pink-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'"
            >
              <i class="fas fa-list mr-1.5"></i>
              My tickets ({{ myTickets.length }})
            </button>
          </div>
        </div>

        <!-- Tab content -->
        <div class="flex-1 overflow-hidden flex flex-col min-h-0">
          <!-- New ticket form -->
          <form v-show="activeTab === 'new'" @submit.prevent="createTicket" class="p-5 space-y-4 overflow-y-auto">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
              <textarea
                v-model="description"
                rows="5"
                class="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-400 transition-colors placeholder:text-gray-400 resize-none"
                placeholder="Describe your issue or request..."
                :disabled="isLoading"
              ></textarea>
            </div>

            <div v-if="error" class="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100">
              {{ error }}
            </div>

            <div v-if="successMessage" class="p-3 bg-green-50 text-green-600 text-sm rounded-lg border border-green-100 flex items-center gap-2">
              <i class="fas fa-check-circle text-green-500"></i>
              {{ successMessage }}
            </div>

            <div class="flex gap-3 pt-2">
              <button 
                type="button"
                @click="emit('close'); resetForm()"
                class="flex-1 px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                :disabled="isLoading"
              >
                Cancel
              </button>
              <button 
                type="submit"
                class="flex-1 px-4 py-2.5 bg-pink-500 text-white rounded-lg font-medium hover:bg-pink-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                :disabled="isLoading"
              >
                <i v-if="isLoading" class="fas fa-spinner fa-spin"></i>
                {{ isLoading ? 'Sending...' : 'Send ticket' }}
              </button>
            </div>
          </form>

          <!-- My tickets list -->
          <div v-show="activeTab === 'list'" class="p-5 overflow-y-auto flex-1 min-h-0">
            <div v-if="loadingTickets" class="flex items-center justify-center py-12">
              <i class="fas fa-spinner fa-spin text-2xl text-pink-500"></i>
            </div>
            <div v-else-if="ticketsError" class="py-8 text-center text-red-500 text-sm">
              {{ ticketsError }}
            </div>
            <div v-else-if="myTickets.length === 0" class="py-12 text-center text-gray-500">
              <i class="fas fa-inbox text-4xl mb-3 opacity-40"></i>
              <p>No tickets yet</p>
              <p class="text-sm mt-1">Create your first ticket in the "New ticket" tab</p>
            </div>
            <div v-else class="space-y-3">
              <div
                v-for="ticket in myTickets"
                :key="ticket.id"
                class="p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
              >
                <div class="flex items-start justify-between gap-2 mb-2">
                  <div class="text-xs text-gray-500">
                    <span>{{ formatDate(ticket.created_at) }}</span>
                    <span v-if="canCompleteTickets && ticket.user" class="block mt-0.5 text-gray-600">
                      {{ ticket.user.name }} ({{ ticket.user.email }})
                    </span>
                  </div>
                  <div class="flex items-center gap-2 shrink-0">
                    <span 
                      class="px-2 py-0.5 text-xs font-medium rounded-full"
                      :class="getStatusBadgeClass(ticket.status)"
                    >
                      {{ ticket.status }}
                    </span>
                    <button
                      v-if="canCompleteTickets && ticket.status !== 'completed' && ticket.status !== 'closed'"
                      @click="completeTicket(ticket.id)"
                      :disabled="completingTicketId === ticket.id"
                      class="px-2 py-1 text-xs font-medium bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 transition-colors"
                    >
                      <i v-if="completingTicketId === ticket.id" class="fas fa-spinner fa-spin"></i>
                      <span v-else>Complete</span>
                    </button>
                    <button
                      v-if="canCompleteTickets && (ticket.status === 'completed' || ticket.status === 'closed')"
                      @click="uncompleteTicket(ticket.id)"
                      :disabled="completingTicketId === ticket.id"
                      class="px-2 py-1 text-xs font-medium bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50 transition-colors"
                    >
                      <i v-if="completingTicketId === ticket.id" class="fas fa-spinner fa-spin"></i>
                      <span v-else>Reopen</span>
                    </button>
                  </div>
                </div>
                <p class="text-sm text-gray-700 whitespace-pre-wrap">{{ ticket.description }}</p>
                <p v-if="ticket.description && ticket.description.length > 200" class="text-xs text-gray-400 mt-1">
                  {{ ticket.description.length }} characters
                </p>

                <!-- Replies (all users can read) -->
                <div v-if="ticket.replies && ticket.replies.length" class="mt-3 pt-3 border-t border-gray-200 space-y-2">
                  <p class="text-xs font-medium text-gray-500 mb-1.5">Replies</p>
                  <div
                    v-for="r in ticket.replies"
                    :key="r.id"
                    class="pl-3 py-2 bg-white rounded border-l-2 border-pink-200 text-sm"
                  >
                    <span class="text-xs text-gray-500">{{ r.user?.name }} · {{ formatDate(r.created_at) }}</span>
                    <p class="mt-0.5 text-gray-700 whitespace-pre-wrap">{{ r.message }}</p>
                  </div>
                </div>

                <!-- Reply form (user 6 only) -->
                <div v-if="canCompleteTickets" class="mt-3 pt-3 border-t border-gray-200">
                  <textarea
                    :value="replyText[ticket.id]"
                    @input="setReply(ticket.id, $event.target.value)"
                    rows="2"
                    placeholder="Write a reply..."
                    class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500/50 resize-none"
                  ></textarea>
                  <button
                    @click="sendReply(ticket.id)"
                    :disabled="sendingReplyTicketId === ticket.id || !(replyText[ticket.id] || '').trim()"
                    class="mt-1.5 px-3 py-1.5 text-xs font-medium bg-pink-500 text-white rounded hover:bg-pink-600 disabled:opacity-50 transition-colors"
                  >
                    <i v-if="sendingReplyTicketId === ticket.id" class="fas fa-spinner fa-spin mr-1"></i>
                    Send reply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
